import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useWindows } from '../../contexts/WindowContext';
import AboutApp from '../Applications/AboutApp/AboutApp';
import SkillsMonitor from '../Applications/SkillsMonitor/SkillsMonitor';
import ProjectsExplorer from '../Applications/ProjectsExplorer/ProjectsExplorer';
import EducationSystem from '../Applications/EducationSystem/EducationSystem';
import AchievementsCenter from '../Applications/AchievementsCenter/AchievementsCenter';
import ContactNetwork from '../Applications/ContactNetwork/ContactNetwork';
import TerminalApp from '../Applications/Terminal/TerminalApp';
import YouTubeChannel from '../Applications/YouTubeChannel/YouTubeChannel';
import MobileHackerGames from '../Applications/HackerGames/MobileHackerGames';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const DrawerOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  z-index: 2000;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: flex-end;
  backdrop-filter: blur(10px);
`;

const DrawerContainer = styled.div<{ isOpen: boolean }>`
  width: 100%;
  height: 70vh;
  background: rgba(0, 0, 0, 0.95);
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border: 2px solid rgba(0, 255, 0, 0.3);
  animation: ${props => props.isOpen ? slideUp : 'none'} 0.3s ease-out;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(0, 255, 0, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 10;
`;

const DrawerTitle = styled.h2`
  color: #00ff00;
  font-family: 'Ubuntu', sans-serif;
  margin: 0;
  font-size: 18px;
`;

const CloseButton = styled.button`
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid #ff0000;
  color: #ff0000;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  
  &:active {
    transform: scale(0.9);
  }
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  padding: 20px;
  padding-bottom: 100px;
`;

const AppCard = styled.div`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:active {
    background: rgba(0, 255, 0, 0.2);
    transform: scale(0.95);
  }
`;

const AppIcon = styled.div`
  font-size: 36px;
  margin-bottom: 10px;
`;

const AppName = styled.div`
  color: #00ff00;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
`;

const AppDescription = styled.div`
  color: #cccccc;
  font-size: 11px;
`;

interface MobileAppDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileAppDrawer: React.FC<MobileAppDrawerProps> = ({ isOpen, onClose }) => {
  const { openWindow } = useWindows();

  const apps = [
    {
      name: 'About',
      icon: '👤',
      description: 'My Profile',
      component: AboutApp,
      title: 'About Mukul'
    },
    {
      name: 'Skills',
      icon: '⚡',
      description: 'Tech Skills',
      component: SkillsMonitor,
      title: 'Skills Monitor'
    },
    {
      name: 'Projects',
      icon: '📁',
      description: 'My Work',
      component: ProjectsExplorer,
      title: 'Projects'
    },
    {
      name: 'Education',
      icon: '🎓',
      description: 'Academic',
      component: EducationSystem,
      title: 'Education'
    },
    {
      name: 'Achievements',
      icon: '🏆',
      description: 'CTF Wins',
      component: AchievementsCenter,
      title: 'Achievements'
    },
    {
      name: 'YouTube',
      icon: '📺',
      description: 'My Channel',
      component: YouTubeChannel,
      title: 'YouTube'
    },
    {
      name: 'Games',
      icon: '🎮',
      description: 'Hacker Games',
      component: MobileHackerGames,
      title: 'Games'
    },
    {
      name: 'Contact',
      icon: '📞',
      description: 'Get in Touch',
      component: ContactNetwork,
      title: 'Contact'
    }
  ];

  const handleAppClick = (app: typeof apps[0]) => {
    openWindow({
      title: app.title,
      component: app.component,
      isMinimized: false,
      isMaximized: false,
      position: { x: 0, y: 0 },
      size: { width: window.innerWidth, height: window.innerHeight }
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <DrawerOverlay isOpen={isOpen} onClick={onClose}>
      <DrawerContainer isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <DrawerHeader>
          <DrawerTitle>📱 Applications</DrawerTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </DrawerHeader>
        
        <AppsGrid>
          {apps.map((app, index) => (
            <AppCard key={index} onClick={() => handleAppClick(app)}>
              <AppIcon>{app.icon}</AppIcon>
              <AppName>{app.name}</AppName>
              <AppDescription>{app.description}</AppDescription>
            </AppCard>
          ))}
        </AppsGrid>
      </DrawerContainer>
    </DrawerOverlay>
  );
};

export default MobileAppDrawer;
